import { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { CONTACT, IMAGES } from "../data/content";
import { MessageCircle, Menu, X } from "lucide-react";

const LINKS = [
  { label: "Our Story", href: "#manifesto", id: "manifesto" },
  { label: "Our Sectors", href: "#sectors", id: "sectors" },
  { label: "Our Approach", href: "#approach", id: "approach" },
  { label: "Our Insights", href: "#insights", id: "insights" },
  { label: "Our Team", href: "#leadership", id: "leadership" },
  { label: "Contact Us", href: "#contact", id: "contact" },
];

export const scrollToSection = (href) => {
  const el = document.querySelector(href);
  if (!el) return;
  if (window.__lenis) window.__lenis.scrollTo(el, { offset: -70, duration: 1.4 });
  else el.scrollIntoView({ behavior: "smooth" });
};

const useScrolled = (threshold = 40) => {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > threshold);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [threshold]);
  return scrolled;
};

const Brand = ({ onClick }) => (
  <a href="#top" onClick={onClick} data-testid="nav-brand" className="flex items-center gap-3 group">
    <img
      src={IMAGES.logoMark}
      alt="Synergy Next"
      data-testid="nav-logo"
      className="h-9 w-auto transition-transform duration-500 group-hover:scale-105"
    />
    <span className="leading-none">
      <span className="block font-sans text-[15px] font-extrabold tracking-[0.04em] text-[#0B2A5B]">
        SYNERGY<span className="text-[#1E5FE0]">NEXT</span>
      </span>
      <span className="block f-label text-[9px] tracking-[0.28em] text-[#1E5FE0] mt-1 whitespace-nowrap">
        STRATEGY. PEOPLE. IMPACT.
      </span>
    </span>
  </a>
);

const WhatsAppButton = ({ testId, className, children }) => (
  <a
    href={CONTACT.whatsappLink}
    target="_blank"
    rel="noopener noreferrer"
    data-testid={testId}
    className={`flex items-center gap-2 rounded-full bg-[#0B2A5B] text-white f-label uppercase whitespace-nowrap hover:bg-[#1E5FE0] transition-colors duration-300 ${className}`}
  >
    {children}
  </a>
);

const DesktopLinks = ({ go }) => (
  <nav className="hidden xl:flex items-center gap-7">
    {LINKS.map((l) => (
      <a
        key={l.id}
        href={l.href}
        onClick={go(l.href)}
        data-testid={`nav-link-${l.id}`}
        className="nav-link f-label text-xs uppercase tracking-[0.14em] whitespace-nowrap text-[#0B2A5B]/70 hover:text-[#0B2A5B] transition-colors"
      >
        {l.label}
      </a>
    ))}
    <WhatsAppButton testId="nav-whatsapp-button" className="text-xs tracking-[0.12em] px-5 py-2.5">
      <MessageCircle className="w-3.5 h-3.5" /> WhatsApp
    </WhatsAppButton>
  </nav>
);

const MobileMenu = ({ go }) => (
  <div className="xl:hidden bg-white/95 backdrop-blur-xl border-t border-[#0B2A5B]/10 px-6 py-6 flex flex-col gap-5">
    {LINKS.map((l) => (
      <a
        key={l.id}
        href={l.href}
        onClick={go(l.href)}
        data-testid={`nav-mobile-link-${l.id}`}
        className="f-label text-sm uppercase tracking-[0.2em] text-[#0B2A5B]"
      >
        {l.label}
      </a>
    ))}
    <WhatsAppButton testId="nav-mobile-whatsapp-button" className="justify-center text-[13px] tracking-[0.15em] px-5 py-3">
      <MessageCircle className="w-4 h-4" /> WhatsApp Connect
    </WhatsAppButton>
  </div>
);

export const Nav = () => {
  const scrolled = useScrolled();
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const navigate = useNavigate();

  const go = (href) => (e) => {
    e.preventDefault();
    setOpen(false);
    if (pathname !== "/") {
      navigate(href === "#top" ? "/" : `/${href}`);
      return;
    }
    scrollToSection(href);
  };

  return (
    <header
      data-testid="site-nav"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-white/85 backdrop-blur-xl border-b border-[#0B2A5B]/10 shadow-[0_8px_30px_-12px_rgba(11,42,91,0.15)]"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 h-[70px] flex items-center justify-between">
        <Brand onClick={go("#top")} />
        <DesktopLinks go={go} />
        <button
          data-testid="nav-mobile-toggle"
          className="xl:hidden text-[#0B2A5B] p-2"
          onClick={() => setOpen(!open)}
          aria-label="Toggle menu"
        >
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>
      {open && <MobileMenu go={go} />}
    </header>
  );
};
